/**
 * The regional sports fee for a ZIP: validation, formatting and the offers
 * request, copied from production so a fee quoted here matches one quoted on
 * the pages that share its cookie. No imports.
 */

export const DEFAULT_OFFERS_PATH = "/shop/api/getOffers";

/**
 * A number must be finite and not negative (zero is a real fee). A string must
 * be digits with an optional two-decimal tail, non-empty, with no minus sign.
 *
 * A TYPE PREDICATE, not a bare boolean: every caller reads an untrusted value —
 * a cookie, a sessionStorage record, a fetch body — and goes straight on to use
 * it as a fee. Narrowing here is what lets `formatRsnFee(fee)` type-check on the
 * far side of the guard (zip-store.ts:232-234) without a cast, and what removes
 * the `as string` the app leaf needed before it became a pure store subscriber.
 */
export function isValidRsnFee(fee: unknown): fee is string | number {
  if (fee === null || fee === undefined || (typeof fee !== "number" && typeof fee !== "string")) {
    return false;
  }
  if (Number.isNaN(Number(fee))) {
    return false;
  }
  if (typeof fee === "number") {
    return Number.isFinite(fee) && fee >= 0;
  }
  return fee !== "" && !fee.includes("-") && /^\d+(\.\d{2})?$/.test(fee);
}

/** Two decimals, "13.99". Throws on an invalid fee — the caller treats that as a failed fetch. */
export function formatRsnFee(fee: string | number): string {
  if (!isValidRsnFee(fee)) {
    throw new Error(`fee ${fee} is not a valid RSN fee`);
  }
  return (typeof fee === "string" ? parseFloat(fee) : fee).toFixed(2);
}

export interface FetchRsnFeeOptions {
  /** Offers endpoint, relative to the page. Defaults to production's. */
  offersPath?: string;
  /** Cancels the request; an aborted fetch resolves to "" like any other failure. */
  signal?: AbortSignal;
}

/**
 * POSTs the offers query for one ZIP and reads the fee off the response.
 * Never rejects: a network or parse failure resolves to "".
 *
 * The status is not checked, as in production. Every `rsnFeeDetails.amount`
 * of zero or more is collected in offer order; the first above zero wins, and
 * if there are only zeros the number 0 comes back. No offers at all is "".
 */
export async function fetchRsnFee(
  zip: string,
  { offersPath = DEFAULT_OFFERS_PATH, signal }: FetchRsnFeeOptions = {}
): Promise<string | number> {
  const requestInit: RequestInit = {
    method: "post",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      appName: "UF",
      "idp-source": "DTVS"
    },
    body: JSON.stringify({
      customerEligibility: { zipcode: [zip] },
      pagination: {
        page: 1,
        limit: 100
      },
      offerActionType: ["Acquisition"],
      salesChannel: ["directvOnline"],
      offerProductTypes: ["video-plan"],
      offerProductFamily: ["OTT"],
      addOnType: ["Programming-Bolt-on", "Bolt-on", "Standalone"],
      planSubType: [
        "cDVR",
        "Cross-Product-Mobility",
        "Streams",
        "CrossProduct-WatchTV",
        "Cross-product",
        "International"
      ],
      contractIndicator: ["RR"],
      creditRisk: "Low"
    }),
    signal
  };

  try {
    const response = await fetch(offersPath, requestInit);
    const body = await response.json();
    const amounts: number[] = [];
    body?.content?.offers?.forEach((offer: any) => {
      const details = offer?.attributes?.rsnFeeDetails;
      if (details !== undefined && details?.amount > -1) {
        amounts.push(details.amount);
      }
    });
    if (amounts.length > 0) {
      return amounts.find(amount => amount > 0) || 0;
    }
    return "";
  } catch {
    return "";
  }
}
